import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { CalendarDays, Check, ChevronDown, ChevronUp, Clock3, Download, Globe, ImagePlus, LogOut, MapPin, MessageCircle, Pencil, Phone, Plus, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import QRCode from "qrcode";

const API = "/api";
const STATUS_OPTIONS = ["new", "contacted", "confirmed", "completed"];
const CLINIC_LABELS = { medicover: "MEDICOVER Hospitals, Hitec City", lux: "LUX Hospitals, Gachibowli" };
const SLOT_LABELS = { "09:00": "9:00 AM", "10:00": "10:00 AM", "11:00": "11:00 AM", "12:00": "12:00 PM", "13:00": "1:00 PM", "14:00": "2:00 PM", "15:00": "3:00 PM", "16:00": "4:00 PM", "17:00": "5:00 PM", "18:00": "6:00 PM", "19:00": "7:00 PM" };
const LOGO_IMAGE = "/logo.png";
const EMPTY_ARTICLE = { title: "", intro: "", symptoms: "", note: "" };
const EMPTY_FAQ = { question: "", answer: "" };

function WhatsAppLinkCard({ headers }) {
  const [connected, setConnected] = useState(false);
  const [qrImage, setQrImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const missRef = useRef(0);
  useEffect(() => {
    let active = true;
    let timer;
    const poll = async () => {
      let isConnected = false;
      try {
        const { data } = await axios.get(`${API}/whatsapp/status`, { headers });
        isConnected = Boolean(data.connected);
        if (!active) return;
        if (isConnected) {
          missRef.current = 0;
          setConnected(true);
          setQrImage(null);
        } else {
          missRef.current += 1;
          // Only show the QR area after two consecutive "not linked" readings,
          // so a brief hiccup never flashes a QR on a linked phone.
          if (missRef.current >= 2) {
            setConnected(false);
            const qrResponse = await axios.get(`${API}/whatsapp/qr`, { headers });
            if (!active) return;
            const qrValue = qrResponse.data.qr;
            if (qrValue) {
              setQrImage(await QRCode.toDataURL(qrValue, { width: 200, margin: 1 }));
              setRefreshedAt(Date.now());
            }
            // if no QR is available yet, keep the previous one on screen
          }
        }
      } catch (error) { /* WhatsApp service may still be starting */ }
      finally { if (active) setLoading(false); }
      if (active) timer = setTimeout(poll, isConnected ? 15000 : 3000);
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, []);
  const resetLink = async () => {
    try {
      await axios.post(`${API}/whatsapp/reset`, {}, { headers });
      setQrImage(null);
      setLoading(true);
      toast.success("Link reset — a fresh QR code is being generated");
    } catch {
      toast.error("Reset failed");
    }
  };
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/whatsapp/test`, {}, { headers });
      setTestResult(data);
      if (data.sent) toast.success("Test alert sent — check your WhatsApp");
      else toast.error("Alert failed — details shown below");
    } catch {
      toast.error("Test request failed");
    } finally {
      setTesting(false);
    }
  };
  return <div className="wa-card" data-testid="whatsapp-link-card">
    <div className="wa-card-head"><span className="wa-icon"><MessageCircle size={18} /></span><div><b>WhatsApp appointment alerts</b><small>{connected ? "Linked — new appointment requests arrive as messages on 7780793371." : "Not linked. Link your WhatsApp once and every new appointment request arrives as a message on your phone."}</small></div>{connected ? <button className="button button-outline wa-reset" data-testid="whatsapp-manage-toggle" onClick={() => setExpanded((v) => !v)}>{expanded ? <>Hide <ChevronUp size={14} /></> : <>Manage link <ChevronDown size={14} /></>}</button> : <button className="button button-outline wa-reset" data-testid="whatsapp-reset-button" onClick={resetLink}>Reset link</button>}</div>
    {connected && !expanded && <p className="wa-connected" data-testid="whatsapp-connected-note"><Check size={14} /> Linked successfully. <button className="wa-test-link" data-testid="whatsapp-test-button" onClick={sendTest} disabled={testing}>{testing ? "Sending…" : "Send test alert"}</button></p>}
    {connected && expanded && <div className="wa-manage" data-testid="whatsapp-manage-panel">
      <p className="wa-connected"><Check size={14} /> Linked successfully. <button className="wa-test-link" data-testid="whatsapp-test-button-expanded" onClick={sendTest} disabled={testing}>{testing ? "Sending…" : "Send test alert"}</button></p>
      <small className="wa-manage-hint">If alerts stop arriving or you change phones, reset the link and scan the fresh QR code below.</small>
      <button className="button button-outline" data-testid="whatsapp-reset-button-expanded" onClick={resetLink}>Reset link</button>
      <div className="wa-qr-area">{qrImage ? <img data-testid="whatsapp-qr-image" src={qrImage} alt="WhatsApp linking QR code" /> : <p data-testid="whatsapp-qr-loading">QR code appears here after you reset the link.</p>}<small><b>Important:</b> In WhatsApp Business, tap <b>⋮ (three dots) → Linked devices → Link a device</b>, and point THAT scanner at this code. Scanning with your phone camera will not work.</small><small className="qr-fresh">The code refreshes automatically every ~20 seconds — scan it promptly after it appears. This page updates itself; no need to refresh.</small></div>
    </div>}
    {!connected && <div className="wa-qr-area">{qrImage ? <img data-testid="whatsapp-qr-image" src={qrImage} alt="WhatsApp linking QR code" /> : <p data-testid="whatsapp-qr-loading">{loading ? "Preparing your QR code…" : "QR code will appear here shortly…"}</p>}<small><b>Important:</b> In WhatsApp Business, tap <b>⋮ (three dots) → Linked devices → Link a device</b>, and point THAT scanner at this code. Scanning with your phone camera will not work.</small><small className="qr-fresh">The code refreshes automatically every ~20 seconds — scan it promptly after it appears. This page updates itself; no need to refresh.</small></div>}
    {testResult && <p className={`wa-test-result ${testResult.sent ? "ok" : "fail"}`} data-testid="whatsapp-test-result">{testResult.sent ? "Delivered to WhatsApp — check the chat named after your own number (Message yourself)." : `Not delivered: ${JSON.stringify(testResult.service_response || testResult.error)}`}</p>}
  </div>;
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("appointments");
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [articles, setArticles] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [articleForm, setArticleForm] = useState(EMPTY_ARTICLE);
  const [editingId, setEditingId] = useState(null);
  const [updateText, setUpdateText] = useState("");
  const [savingContent, setSavingContent] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [draftTopic, setDraftTopic] = useState("");
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [galleryCaption, setGalleryCaption] = useState("");
  const [galleryFile, setGalleryFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [faqForm, setFaqForm] = useState(EMPTY_FAQ);
  const [editingFaqId, setEditingFaqId] = useState(null);
  const [posters, setPosters] = useState([]);
  const [posterTopic, setPosterTopic] = useState("");
  const [generatingPoster, setGeneratingPoster] = useState(false);

  const authHeader = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/appointments`, { headers: authHeader });
      setAppointments(data);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("admin_token");
        setToken("");
      } else {
        toast.error("Could not load appointments");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadContent = useCallback(async () => {
    try {
      const [articlesRes, updatesRes, galleryRes, faqsRes, postersRes] = await Promise.all([
        axios.get(`${API}/admin/articles`, { headers: authHeader }),
        axios.get(`${API}/updates`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/faqs`),
        axios.get(`${API}/admin/posters`, { headers: authHeader }),
      ]);
      setArticles(articlesRes.data);
      setUpdates(updatesRes.data);
      setGallery(galleryRes.data);
      setFaqs(faqsRes.data);
      setPosters(postersRes.data);
    } catch {
      toast.error("Could not load website content");
    }
  }, [token]);

  useEffect(() => {
    let link = document.querySelector("link[rel='manifest']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      link.href = "/manifest.json";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => { load(); loadContent(); }, [load, loadContent]);

  const login = async (event) => {
    event.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("admin_token", data.token);
      setToken(data.token);
      setPassword("");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/appointments/${id}`, { status }, { headers: authHeader });
      setAppointments((list) => list.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this appointment request?")) return;
    try {
      await axios.delete(`${API}/admin/appointments/${id}`, { headers: authHeader });
      setAppointments((list) => list.filter((a) => a.id !== id));
      toast.success("Request deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportCsv = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/appointments/export`, { headers: authHeader, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "appointments.csv";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Spreadsheet downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const saveArticle = async (event) => {
    event.preventDefault();
    setSavingContent(true);
    const payload = {
      title: articleForm.title,
      intro: articleForm.intro,
      note: articleForm.note,
      symptoms: articleForm.symptoms.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editingId) {
        await axios.put(`${API}/admin/articles/${editingId}`, payload, { headers: authHeader });
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/admin/articles`, payload, { headers: authHeader });
        toast.success("Article published");
      }
      setArticleForm(EMPTY_ARTICLE);
      setEditingId(null);
      loadContent();
    } catch {
      toast.error("Could not save article");
    } finally {
      setSavingContent(false);
    }
  };

  const editArticle = (article) => {
    setEditingId(article.id);
    setArticleForm({ title: article.title, intro: article.intro, note: article.note, symptoms: article.symptoms.join("\n") });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const publishArticle = async (id) => {
    try {
      await axios.post(`${API}/admin/articles/${id}/publish`, {}, { headers: authHeader });
      toast.success("Article published on the website");
      loadContent();
    } catch {
      toast.error("Publish failed");
    }
  };

  const generateDraft = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/admin/articles/generate`, draftTopic.trim() ? { topic: draftTopic.trim() } : {}, { headers: authHeader });
      setDraftTopic("");
      toast.success("New draft ready for review");
      loadContent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Draft generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const regenerateDraft = async (id) => {
    if (!window.confirm("Replace this draft with a freshly written one?")) return;
    setRegeneratingId(id);
    try {
      await axios.delete(`${API}/admin/articles/${id}`, { headers: authHeader });
      await axios.post(`${API}/admin/articles/generate`, {}, { headers: authHeader });
      toast.success("A fresh draft is ready");
      loadContent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not write a new draft");
    } finally {
      setRegeneratingId(null);
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article from the website?")) return;
    try {
      await axios.delete(`${API}/admin/articles/${id}`, { headers: authHeader });
      setArticles((list) => list.filter((a) => a.id !== id));
      toast.success("Article deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const addUpdate = async (event) => {
    event.preventDefault();
    if (!updateText.trim()) return;
    try {
      await axios.post(`${API}/admin/updates`, { text: updateText.trim() }, { headers: authHeader });
      setUpdateText("");
      loadContent();
      toast.success("Information published on the website");
    } catch {
      toast.error("Could not publish");
    }
  };

  const deleteUpdate = async (id) => {
    try {
      await axios.delete(`${API}/admin/updates/${id}`, { headers: authHeader });
      setUpdates((list) => list.filter((u) => u.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const uploadPhoto = async (event) => {
    event.preventDefault();
    if (!galleryFile || !galleryCaption.trim()) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", galleryFile);
      formData.append("caption", galleryCaption.trim());
      await axios.post(`${API}/admin/gallery`, formData, { headers: authHeader });
      setGalleryFile(null);
      setGalleryCaption("");
      if (event.target.reset) event.target.reset();
      toast.success("Photo added to the website gallery");
      loadContent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Remove this photo from the website gallery?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { headers: authHeader });
      setGallery((list) => list.filter((p) => p.id !== id));
      toast.success("Photo removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const generatePoster = async (event) => {
    event.preventDefault();
    if (!posterTopic.trim()) return;
    setGeneratingPoster(true);
    try {
      const { data } = await axios.post(`${API}/admin/posters/generate`, { topic: posterTopic.trim() }, { headers: authHeader, timeout: 240000 });
      setPosters((list) => [data, ...list]);
      setPosterTopic("");
      toast.success("Poster ready — preview below, then download");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Poster generation failed — please try again");
    } finally {
      setGeneratingPoster(false);
    }
  };

  const deletePoster = async (id) => {
    if (!window.confirm("Delete this poster?")) return;
    try {
      await axios.delete(`${API}/admin/posters/${id}`, { headers: authHeader });
      setPosters((list) => list.filter((p) => p.id !== id));
      toast.success("Poster deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const togglePosterOnWebsite = async (poster) => {
    try {
      if (poster.gallery_path) {
        await axios.delete(`${API}/admin/posters/${poster.id}/unpublish`, { headers: authHeader });
        setPosters((list) => list.map((p) => (p.id === poster.id ? { ...p, gallery_path: null } : p)));
        toast.success("Removed from the website gallery");
      } else {
        const { data } = await axios.post(`${API}/admin/posters/${poster.id}/publish`, {}, { headers: authHeader });
        setPosters((list) => list.map((p) => (p.id === poster.id ? { ...p, gallery_path: data.gallery_path } : p)));
        toast.success("Poster is now on the website gallery");
      }
    } catch {
      toast.error("Could not update the website gallery");
    }
  };

  const saveFaq = async (event) => {
    event.preventDefault();
    setSavingContent(true);
    try {
      if (editingFaqId) {
        await axios.put(`${API}/admin/faqs/${editingFaqId}`, faqForm, { headers: authHeader });
        toast.success("FAQ updated");
      } else {
        await axios.post(`${API}/admin/faqs`, faqForm, { headers: authHeader });
        toast.success("FAQ added to the website");
      }
      setFaqForm(EMPTY_FAQ);
      setEditingFaqId(null);
      loadContent();
    } catch {
      toast.error("Could not save FAQ");
    } finally {
      setSavingContent(false);
    }
  };

  const editFaq = (faq) => {
    setEditingFaqId(faq.id);
    setFaqForm({ question: faq.question, answer: faq.answer });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteFaq = async (id) => {
    if (!window.confirm("Delete this FAQ from the website?")) return;
    try {
      await axios.delete(`${API}/admin/faqs/${id}`, { headers: authHeader });
      setFaqs((list) => list.filter((f) => f.id !== id));
      toast.success("FAQ deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const moveItem = async (collection, list, index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    const reordered = [...list];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    try {
      await axios.put(`${API}/admin/reorder`, { collection, ids: reordered.map((x) => x.id) }, { headers: authHeader });
      loadContent();
    } catch {
      toast.error("Could not save the new order");
    }
  };

  const publishedArticles = articles.filter((a) => a.status !== "draft");
  const moveArticle = (index, direction) => moveItem("articles", publishedArticles, index, direction);
  const moveFaq = (index, direction) => moveItem("faqs", faqs, index, direction);
  const movePhoto = (index, direction) => moveItem("gallery", gallery, index, direction);

  const logout = () => {
    sessionStorage.removeItem("admin_token");
    setToken("");
  };

  if (!token) {
    return <div className="admin-shell"><Toaster position="top-center" />
      <form className="admin-login" onSubmit={login} data-testid="admin-login-form">
        <img className="brand-logo" data-testid="admin-login-logo" src={LOGO_IMAGE} alt="Dr. Sreeniketh Reddy — Urologist" />
        <h1>Clinic Manager</h1>
        <p>Private area for Dr. Sreeniketh Reddy. Manage appointments, articles and website information.</p>
        <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin password" />
        {error && <p className="admin-error" data-testid="admin-login-error">{error}</p>}
        <button className="button button-primary full" data-testid="admin-login-button">Sign in</button>
        <small className="install-hint">On your phone: open this page in Chrome or Safari, then choose "Add to Home Screen" to use it as an app.</small>
      </form>
    </div>;
  }

  return <div className="admin-shell wide"><Toaster position="top-center" />
    <header className="admin-header">
      <div className="brand"><img className="brand-logo" data-testid="admin-header-logo" src={LOGO_IMAGE} alt="Dr. Sreeniketh Reddy — Urologist" /><span><b>Clinic Manager</b><small>Private dashboard</small></span></div>
      <div className="admin-actions">
        {tab === "appointments" && <button className="button button-outline" data-testid="admin-export-button" onClick={exportCsv}><Download size={15} /> Export</button>}
        <button className="button button-outline" data-testid="admin-refresh-button" onClick={() => { load(); loadContent(); }}><RefreshCw size={15} /> Refresh</button>
        <button className="button button-outline" data-testid="admin-logout-button" onClick={logout}><LogOut size={15} /> Sign out</button>
      </div>
    </header>
    <WhatsAppLinkCard headers={authHeader} />
    <div className="admin-tabs" data-testid="admin-tabs">
      <button data-testid="tab-appointments" className={tab === "appointments" ? "active" : ""} onClick={() => setTab("appointments")}>Appointments</button>
      <button data-testid="tab-articles" className={tab === "articles" ? "active" : ""} onClick={() => setTab("articles")}>Articles</button>
      <button data-testid="tab-faqs" className={tab === "faqs" ? "active" : ""} onClick={() => setTab("faqs")}>FAQs</button>
      <button data-testid="tab-updates" className={tab === "updates" ? "active" : ""} onClick={() => setTab("updates")}>Clinic info</button>
      <button data-testid="tab-gallery" className={tab === "gallery" ? "active" : ""} onClick={() => setTab("gallery")}>Gallery</button>
      <button data-testid="tab-posters" className={tab === "posters" ? "active" : ""} onClick={() => setTab("posters")}>Posters</button>
    </div>

    {tab === "appointments" && (
      loading && appointments.length === 0 ? <p className="admin-empty">Loading…</p> :
      appointments.length === 0 ? <p className="admin-empty" data-testid="admin-empty">No appointment requests yet.</p> :
      <div className="admin-list" data-testid="admin-appointments-list">{appointments.map((a) => (
        <article className="admin-card" key={a.id} data-testid={`admin-appointment-${a.id}`}>
          <div className="admin-card-main">
            <b>{a.name}</b>
            <span className="admin-line"><Phone size={13} /> <a href={`tel:${a.phone}`}>{a.phone}</a></span>
            <span className="admin-line"><CalendarDays size={13} /> Preferred date: {a.preferred_date}{a.time_slot ? ` · ${SLOT_LABELS[a.time_slot] || a.time_slot}` : ""}</span>
            {a.clinic && <span className="admin-line"><MapPin size={13} /> {CLINIC_LABELS[a.clinic] || a.clinic}</span>}
            <span className="admin-line"><Clock3 size={13} /> Requested {new Date(a.created_at).toLocaleString("en-IN")}</span>
            <p className="admin-concern">{a.concern}</p>
          </div>
          <div className="admin-card-side">
            <span className={`status-pill status-${a.status || "new"}`} data-testid={`admin-status-${a.id}`}>{a.status || "new"}</span>
            <select data-testid={`admin-status-select-${a.id}`} value={a.status || "new"} onChange={(e) => setStatus(a.id, e.target.value)}>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button className="admin-delete" data-testid={`admin-delete-${a.id}`} onClick={() => remove(a.id)}><Trash2 size={15} /></button>
          </div>
        </article>))}
      </div>
    )}

    {tab === "articles" && (
      <div data-testid="articles-panel">
        <div className="editor-card">
          <b>Daily AI article drafts</b>
          <p className="editor-hint">A new draft is written automatically every day. Review it below and publish with one tap — nothing appears on the website without your approval. Want a specific topic? Type it below, or leave it blank and let AI choose.</p>
          <label>Topic (optional)<input data-testid="draft-topic-input" value={draftTopic} onChange={(e) => setDraftTopic(e.target.value)} placeholder="e.g. kidney stone diet, prostate exercises" /></label>
          <div className="editor-actions">
            <button type="button" className="button button-outline" data-testid="generate-draft-button" disabled={generating} onClick={generateDraft}>{generating ? "Writing a draft…" : "Write a draft now"}</button>
          </div>
        </div>
        {articles.filter((a) => a.status === "draft").map((d) => (
          <div className="editor-card draft-card" key={d.id} data-testid={`draft-card-${d.id}`}>
            <span className="status-pill status-draft" data-testid={`draft-badge-${d.id}`}>Draft — not on website yet</span>
            <b>{d.title}</b>
            <p>{d.intro}</p>
            <ul>{d.symptoms.map((s) => <li key={s}>{s}</li>)}</ul>
            <p className="draft-note">{d.note}</p>
            <div className="editor-actions">
              <button className="button button-primary" data-testid={`draft-publish-${d.id}`} onClick={() => publishArticle(d.id)}>Publish to website</button>
              <button className="button button-outline" data-testid={`draft-regenerate-${d.id}`} disabled={regeneratingId === d.id} onClick={() => regenerateDraft(d.id)}>{regeneratingId === d.id ? "Writing…" : "Try another version"}</button>
              <button className="icon-btn" data-testid={`draft-edit-${d.id}`} onClick={() => editArticle(d)}><Pencil size={14} /></button>
              <button className="icon-btn danger" data-testid={`draft-delete-${d.id}`} onClick={() => deleteArticle(d.id)}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        <form className="editor-card" onSubmit={saveArticle} data-testid="article-form">
          <b>{editingId ? "Edit article" : "Write a new article"}</b>
          <p className="editor-hint">Published articles appear instantly in the Common symptoms section of the website. Use the arrows to change their order.</p>
          <label>Title<input data-testid="article-title-input" required value={articleForm.title} onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })} placeholder="e.g. Symptoms of prostate cancer" /></label>
          <label>Short introduction<input data-testid="article-intro-input" required value={articleForm.intro} onChange={(e) => setArticleForm({ ...articleForm, intro: e.target.value })} placeholder="One or two lines a patient can understand" /></label>
          <label>Symptoms (one per line)<textarea data-testid="article-symptoms-input" required rows="5" value={articleForm.symptoms} onChange={(e) => setArticleForm({ ...articleForm, symptoms: e.target.value })} placeholder={"Weak urine stream\nFrequent urination at night"} /></label>
          <label>Closing advice note<input data-testid="article-note-input" required value={articleForm.note} onChange={(e) => setArticleForm({ ...articleForm, note: e.target.value })} placeholder="e.g. Early evaluation makes treatment simpler." /></label>
          <div className="editor-actions">
            <button className="button button-primary" data-testid="article-save-button" disabled={savingContent}>{savingContent ? "Saving…" : editingId ? "Update article" : "Publish article"}</button>
            {editingId && <button type="button" className="button button-outline" data-testid="article-cancel-button" onClick={() => { setEditingId(null); setArticleForm(EMPTY_ARTICLE); }}>Cancel</button>}
          </div>
        </form>
        {publishedArticles.map((a, index) => (
          <div className="admin-row" key={a.id} data-testid={`admin-article-${a.id}`}>
            <div><b>{a.title}</b><p>{a.intro}</p></div>
            <div className="row-actions">
              <button className="icon-btn" data-testid={`article-up-${a.id}`} disabled={index === 0} onClick={() => moveArticle(index, -1)}><ChevronUp size={14} /></button>
              <button className="icon-btn" data-testid={`article-down-${a.id}`} disabled={index === publishedArticles.length - 1} onClick={() => moveArticle(index, 1)}><ChevronDown size={14} /></button>
              <button className="icon-btn" data-testid={`article-edit-${a.id}`} onClick={() => editArticle(a)}><Pencil size={14} /></button>
              <button className="icon-btn danger" data-testid={`article-delete-${a.id}`} onClick={() => deleteArticle(a.id)}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    )}

    {tab === "faqs" && (
      <div data-testid="faqs-panel">
        <form className="editor-card" onSubmit={saveFaq} data-testid="faq-form">
          <b>{editingFaqId ? "Edit FAQ" : "Add a new FAQ"}</b>
          <p className="editor-hint">FAQs appear in the "Good to know" section of the website. Use the arrows to change their order.</p>
          <label>Question<input data-testid="faq-question-input" required maxLength={200} value={faqForm.question} onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })} placeholder="e.g. Is kidney stone surgery painful?" /></label>
          <label>Answer<textarea data-testid="faq-answer-input" required rows={3} maxLength={1200} value={faqForm.answer} onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })} placeholder="Write a short, reassuring answer" /></label>
          <div className="editor-actions">
            <button className="button button-primary" data-testid="faq-save-button" disabled={savingContent}>{savingContent ? "Saving…" : editingFaqId ? "Update FAQ" : "Add FAQ"}</button>
            {editingFaqId && <button type="button" className="button button-outline" data-testid="faq-cancel-button" onClick={() => { setEditingFaqId(null); setFaqForm(EMPTY_FAQ); }}>Cancel</button>}
          </div>
        </form>
        {faqs.length === 0 ? <p className="admin-empty" data-testid="faqs-empty">No FAQs yet — add your first one above.</p> :
          faqs.map((f, index) => (
            <div className="admin-row" key={f.id} data-testid={`admin-faq-${f.id}`}>
              <div><b>{f.question}</b><p>{f.answer}</p></div>
              <div className="row-actions">
                <button className="icon-btn" data-testid={`faq-up-${f.id}`} disabled={index === 0} onClick={() => moveFaq(index, -1)}><ChevronUp size={14} /></button>
                <button className="icon-btn" data-testid={`faq-down-${f.id}`} disabled={index === faqs.length - 1} onClick={() => moveFaq(index, 1)}><ChevronDown size={14} /></button>
                <button className="icon-btn" data-testid={`faq-edit-${f.id}`} onClick={() => editFaq(f)}><Pencil size={14} /></button>
                <button className="icon-btn danger" data-testid={`faq-delete-${f.id}`} onClick={() => deleteFaq(f.id)}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
      </div>
    )}

    {tab === "updates" && (
      <div data-testid="updates-panel">
        <form className="editor-card" onSubmit={addUpdate} data-testid="update-form">
          <b>Share information on the website</b>
          <p className="editor-hint">Shown on the homepage under "From the clinic". Example: "Evening OPD at LUX Hospitals now available on Saturdays."</p>
          <label>Information<textarea data-testid="update-text-input" required rows={3} maxLength={280} value={updateText} onChange={(e) => setUpdateText(e.target.value)} placeholder="Write a short note for patients (max 280 characters)" /></label>
          <div className="editor-actions">
            <button className="button button-primary" data-testid="update-publish-button"><Plus size={15} /> Publish</button>
          </div>
        </form>
        {updates.length === 0 ? <p className="admin-empty" data-testid="updates-empty">No information published yet.</p> :
          updates.map((u) => (
            <div className="admin-row" key={u.id} data-testid={`admin-update-${u.id}`}>
              <div><b>{u.text}</b><p>{new Date(u.created_at).toLocaleString("en-IN")}</p></div>
              <div className="row-actions">
                <button className="icon-btn danger" data-testid={`update-delete-${u.id}`} onClick={() => deleteUpdate(u.id)}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
      </div>
    )}

    {tab === "posters" && (
      <div data-testid="posters-panel">
        <form className="editor-card" onSubmit={generatePoster} data-testid="poster-form">
          <b>Create a patient-education poster</b>
          <p className="editor-hint">Type a topic — AI writes the key points and draws a matching illustration, then builds a branded poster with your photo, hospital details and phone number. Takes about a minute. Download and share on WhatsApp or Instagram.</p>
          <label>Topic<input data-testid="poster-topic-input" required maxLength={120} value={posterTopic} onChange={(e) => setPosterTopic(e.target.value)} placeholder="e.g. Kidney stone prevention in summer" disabled={generatingPoster} /></label>
          <div className="editor-actions">
            <button className="button button-primary" data-testid="poster-generate-button" disabled={generatingPoster}><Sparkles size={15} /> {generatingPoster ? "Creating your poster… (about a minute)" : "Generate poster"}</button>
          </div>
        </form>
        {posters.length === 0 ? <p className="admin-empty" data-testid="posters-empty">No posters yet — create your first one above.</p> :
          <div className="admin-gallery-grid">{posters.map((p) => (
            <div className="admin-gallery-item" key={p.id} data-testid={`admin-poster-${p.id}`}>
              <img src={`${API}/gallery/image/${p.storage_path}`} alt={p.title || p.topic} loading="lazy" />
              <div><span>{p.title || p.topic}</span>
                <button className={`icon-btn ${p.gallery_path ? "active" : ""}`} data-testid={`poster-publish-${p.id}`} title={p.gallery_path ? "Remove from website" : "Add to website gallery"} onClick={() => togglePosterOnWebsite(p)}>{p.gallery_path ? <Check size={14} /> : <Globe size={14} />}</button>
                <a className="icon-btn" data-testid={`poster-download-${p.id}`} href={`${API}/gallery/image/${p.storage_path}`} download={`${(p.title || "poster").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`}><Download size={14} /></a>
                <button className="icon-btn danger" data-testid={`poster-delete-${p.id}`} onClick={() => deletePoster(p.id)}><Trash2 size={14} /></button>
              </div>
            </div>))}
          </div>}
      </div>
    )}

    {tab === "gallery" && (
      <div data-testid="gallery-panel">
        <form className="editor-card" onSubmit={uploadPhoto} data-testid="gallery-form">
          <b>Add a photo or video to the website gallery</b>
          <p className="editor-hint">Conference photos, workshop moments, news clippings or short video clips (photos under 10 MB, videos under 50 MB) with a short caption. New uploads appear first; use the arrows to rearrange.</p>
          <label>Photo or video<input data-testid="gallery-file-input" type="file" accept="image/*,video/*" required onChange={(e) => setGalleryFile(e.target.files?.[0] || null)} /></label>
          <label>Caption<input data-testid="gallery-caption-input" required maxLength={160} value={galleryCaption} onChange={(e) => setGalleryCaption(e.target.value)} placeholder="e.g. Robotic surgery workshop, Hyderabad 2026" /></label>
          <div className="editor-actions">
            <button className="button button-primary" data-testid="gallery-upload-button" disabled={uploading}><ImagePlus size={15} /> {uploading ? "Uploading…" : "Add to gallery"}</button>
          </div>
        </form>
        {gallery.length === 0 ? <p className="admin-empty" data-testid="gallery-empty">No photos yet — add your first one above.</p> :
          <div className="admin-gallery-grid">{gallery.map((p, index) => (
            <div className="admin-gallery-item" key={p.id} data-testid={`admin-gallery-${p.id}`}>
              {p.content_type?.startsWith("video/") ? <video controls playsInline preload="metadata" src={`${API}/gallery/image/${p.storage_path}`} /> : <img src={`${API}/gallery/image/${p.storage_path}`} alt={p.caption} loading="lazy" />}
              <div><span>{p.caption}</span>
                <button className="icon-btn" data-testid={`gallery-up-${p.id}`} disabled={index === 0} onClick={() => movePhoto(index, -1)}><ChevronUp size={14} /></button>
                <button className="icon-btn" data-testid={`gallery-down-${p.id}`} disabled={index === gallery.length - 1} onClick={() => movePhoto(index, 1)}><ChevronDown size={14} /></button>
                <button className="icon-btn danger" data-testid={`gallery-delete-${p.id}`} onClick={() => deletePhoto(p.id)}><Trash2 size={14} /></button>
              </div>
            </div>))}
          </div>}
      </div>
    )}
  </div>;
}
